import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { lazy, Suspense, useMemo } from "react";
import { useAppSelector } from "@store/hooks";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { themeSettings } from "@shared/utils/theme";
import { ErrorBoundary } from "@shared/components/ErrorBoundary";
import { NotificationProvider } from "@shared/contexts/NotificationContext";
import ArgStudioScreen from "@features/annogram/components/ArgStudioScreen";
import LearnLayout from "@features/learn/components/LearnLayout";
// Lazy: this pulls react-markdown plus every Learn page. Loading it
// eagerly put ~218 kB (62 kB gzipped) into the main chunk for a route
// most sessions never open. Import it — or anything in
// features/learn/learn.ts — statically and that returns.
const LearnScreen = lazy(
  () => import("@features/learn/components/LearnScreen")
);

/** Carries the slug across the /concepts → /learn move. */
const RedirectToLearnPage = () => {
  const { slug } = useParams<{ slug?: string }>();
  return <Navigate to={slug ? `/learn/${slug}` : "/learn"} replace />;
};
//import DashboardScreen from "./screens/DashboardScreen";
import Landing from "@features/landing/components/Layout";
import { CssBaseline } from "@mui/material";
import MainScreen from "@features/landing/screens/MainScreen";
import FilesScreen from "@features/documents/components/FilesScreen";
import ProfileScreen from "@features/profile/components/ProfileScreen";
import AuthContext from "@shared/contexts/AuthContext";
import WorkspaceDataFetcher from "./workspace-loader/WorkspaceDataFetcher";
import WorkspaceLoadingSpinner from "./workspace-loader/WorkspaceLoadingSpinner";
import AdminPanel from "./admin-panel/Layout";
import LegalDisclaimerScreen from "@features/landing/screens/LegalDisclaimerScreen";
import GoodbyeScreen from "@features/landing/screens/GoodbyeScreen";
import ResetPasswordScreen from "@features/landing/screens/ResetPasswordScreen";
import { WorkspaceReadingsScreen } from "@features/workspace-readings/components/WorkspaceReadingsScreen";
import OrgSettingsScreen from "@features/orgs/components/OrgSettingsScreen";
import OrgJoinScreen from "@features/orgs/components/OrgJoinScreen";
import RequireAuth from "@shared/components/RequireAuth";
import StepUpDialog from "@shared/components/StepUpDialog";
import WorkspacesLayout from "@features/workspaces/components/WorkspacesLayout";
import ResourcesScreen from "@features/workspaces/components/ResourcesScreen";
import MembersScreen from "@features/workspaces/components/MembersScreen";
import WorkspaceSettingsScreen from "@features/workspaces/components/WorkspaceSettingsScreen";
import WorkspaceJoinScreen from "@features/workspaces/components/WorkspaceJoinScreen";
function App() {
  const mode = useAppSelector((state) => state.theme.mode);

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return (
    <ErrorBoundary>
      {/* Opt into React Router v7 behaviors early and silence the
          future-flag warnings — fewer surprises at the v7 bump.
          startTransition wraps router state updates (React 18
          concurrent-friendly); relativeSplatPath is a no-op for this
          app, which has no splat routes. */}
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <WorkspaceDataFetcher />
            <WorkspaceLoadingSpinner />
            <StepUpDialog />
            <AuthContext>
              <Routes>
                <Route element={<Landing />}>
                  <Route path="/" element={<MainScreen />} />
                  <Route
                    path="documentation"
                    element={<Navigate to="/learn" replace />}
                  />
                  <Route
                    path="terms"
                    element={<LegalDisclaimerScreen doc="terms" />}
                  />
                  <Route
                    path="privacy"
                    element={<LegalDisclaimerScreen doc="privacy" />}
                  />
                  {/* Old combined-tabs URL; keep links alive. */}
                  <Route
                    path="legal"
                    element={<Navigate to="/terms" replace />}
                  />
                </Route>
                {/* Readable without an account: the demo links into these,
                    and a shared link should resolve for anyone. Outside the
                    landing layout — see LearnLayout for why. `:slug` serves
                    both section indexes (/learn/method) and pages, so page
                    URLs stay flat and survive being reclassified. */}
                <Route path="learn" element={<LearnLayout />}>
                  <Route
                    index
                    element={
                      <Suspense fallback={null}>
                        <LearnScreen />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":slug"
                    element={
                      <Suspense fallback={null}>
                        <LearnScreen />
                      </Suspense>
                    }
                  />
                </Route>
                {/* Legacy paths. /help and /documentation pointed at a
                    reference manual that documented controls which no
                    longer existed; /concepts was this library's first
                    home. All resolve to Learn, and deliberately outside
                    RequireAuth — Learn is public, so an old link should
                    not bounce a signed-out reader to the landing page. */}
                <Route path="help" element={<Navigate to="/learn" replace />} />
                <Route path="concepts" element={<Navigate to="/learn" replace />} />
                <Route path="concepts/:slug" element={<RedirectToLearnPage />} />
                <Route path="annogram" element={<ArgStudioScreen />} />
                <Route path="orgs/join/:token" element={<OrgJoinScreen />} />
                <Route path="workspaces/join/:token" element={<WorkspaceJoinScreen />} />
                <Route path="resetpassword" element={<ResetPasswordScreen />} />
                <Route path="goodbye" element={<GoodbyeScreen />} />
                <Route element={<RequireAuth />}>
                  <Route element={<AdminPanel />}>
                    <Route path="workspaces" element={<WorkspacesLayout />}>
                      <Route index element={<Navigate to="resources" replace />} />
                      <Route path="resources" element={<ResourcesScreen />} />
                      <Route path="members" element={<MembersScreen />} />
                      <Route path="settings" element={<WorkspaceSettingsScreen />} />
                      {/* /workspaces/documents → /files. The Documents tab
                          used to live here but documents are user-owned,
                          not workspace-scoped — see /files for the
                          canonical library view. */}
                      <Route path="documents" element={<Navigate to="/files" replace />} />
                    </Route>
                    <Route path="files" element={<FilesScreen />} />
                    <Route path="documents" element={<Navigate to="/files" replace />} />
                    <Route path="profile" element={<ProfileScreen />} />
                    <Route path="workspace/:workspaceId/readings" element={<WorkspaceReadingsScreen />} />
                    <Route path="orgs/:slug/settings" element={<OrgSettingsScreen />} />
                    {/* <Route path="dashboard" element={<DashboardScreen />} /> */}
                  </Route>
                </Route>
              </Routes>
            </AuthContext>
          </NotificationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
